import { parseActivityEmailReference } from '@/lib/activity-type-maps';
import type { GetRecentActivitiesApiResponse, RecentActivityListItemApiResponse } from '@/types/api/recent-activities';
import { useApi } from './useApi';
import { useModeAwareQuery } from './useModeAwareQuery';

export interface TransactionActivity {
  id: string;
  type: 'note' | 'communication' | 'collection' | 'promise' | 'dispute' | 'system';
  title: string;
  description: string;
  createdBy: string;
  createdAt: Date;
  /** Canonical note id, set only on note entries that can be edited or deleted. */
  noteId?: string;
  emailJobId?: number;
  emailChannel?: string;
  metadata?: {
    method?: 'email' | 'phone' | 'sms';
    status?: string;
    amount?: number;
    dueDate?: string;
    creationNote?: string;
  };
}

const ACTIVITY_TYPE_MAP: Record<string, TransactionActivity['type']> = {
  note: 'note',
  email: 'communication',
  phone: 'communication',
  sms: 'communication',
  communication: 'communication',
  case: 'collection',
  promise: 'promise',
  dispute: 'dispute',
  system: 'system',
  status_change: 'system',
  payment: 'system',
};

function mapApiToTransactionActivity(item: RecentActivityListItemApiResponse): TransactionActivity {
  const type = ACTIVITY_TYPE_MAP[item.activityType] ?? 'system';

  let metadata: TransactionActivity['metadata'];
  if (item.metadataJson) {
    try {
      const parsed = JSON.parse(item.metadataJson) as Record<string, unknown>;
      metadata = {
        method: typeof parsed.method === 'string' ? (parsed.method as 'email' | 'phone' | 'sms') : undefined,
        status: typeof parsed.status === 'string' ? parsed.status : undefined,
        amount: typeof parsed.amount === 'number' ? parsed.amount : undefined,
        dueDate: typeof parsed.dueDate === 'string' ? parsed.dueDate : undefined,
        creationNote:
          typeof parsed.creationNote === 'string' && parsed.creationNote.trim().length > 0
            ? parsed.creationNote.trim()
            : undefined,
      };
      // Only keep metadata if it has at least one defined value
      if (
        !metadata.method &&
        !metadata.status &&
        metadata.amount === undefined &&
        !metadata.dueDate &&
        !metadata.creationNote
      ) {
        metadata = undefined;
      }
    } catch {
      // ignore malformed metadata
    }
  }

  const emailReference = parseActivityEmailReference(item.metadataJson);

  return {
    id: String(item.id),
    type,
    title: item.title,
    description: item.description,
    createdBy: item.actorName,
    createdAt: new Date(item.createdAt),
    emailJobId: emailReference?.emailJobId,
    emailChannel: emailReference?.emailChannel,
    metadata,
  };
}

function buildMockActivities(transactionAmount: number): TransactionActivity[] {
  const items: TransactionActivity[] = [
    {
      id: 'mock-1',
      type: 'note',
      title: 'Payment reminder sent',
      description: 'Automated payment reminder sent to customer. Invoice is 5 days overdue.',
      createdBy: 'System',
      createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
    },
    {
      id: 'mock-2',
      type: 'communication',
      title: 'Email sent to customer',
      description: 'Follow-up email sent regarding outstanding payment. Customer acknowledged receipt.',
      createdBy: 'Sarah Johnson',
      createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
      emailJobId: 1,
      emailChannel: 'Queued',
      metadata: {
        method: 'email',
        status: 'delivered',
      },
    },
    {
      id: 'mock-3',
      type: 'communication',
      title: 'Phone call with customer',
      description: 'Spoke with accounts payable. Customer confirmed they will process payment by end of week.',
      createdBy: 'Mike Chen',
      createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
      metadata: {
        method: 'phone',
        status: 'completed',
      },
    },
    {
      id: 'mock-4',
      type: 'collection',
      title: 'Added to collection case',
      description: 'Transaction added to collection case AR-00123 due to non-payment.',
      createdBy: 'System',
      createdAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000),
      metadata: {
        creationNote: 'Customer has disputed two of the invoices. Chasing for payment on the remaining balance.',
      },
    },
    {
      id: 'mock-5',
      type: 'promise',
      title: 'Promise to pay recorded',
      description: 'Customer promised to pay full amount by end of month. Confirmation received via phone.',
      createdBy: 'Sarah Johnson',
      createdAt: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000),
      metadata: {
        amount: transactionAmount,
        dueDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(),
        creationNote: 'Finance director confirmed the payment run is scheduled for month end.',
      },
    },
    {
      id: 'mock-6',
      type: 'dispute',
      title: 'Dispute raised',
      description: 'Customer disputed invoice amount. Claims services were not delivered as agreed.',
      createdBy: 'Mike Chen',
      createdAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000),
      metadata: {
        status: 'under_review',
        creationNote: 'Customer claims two line items were never delivered. Awaiting proof of delivery.',
      },
    },
    {
      id: 'mock-7',
      type: 'dispute',
      title: 'Dispute resolved',
      description: 'Dispute resolved in favor of company. Customer agreed to pay full amount.',
      createdBy: 'Sarah Johnson',
      createdAt: new Date(Date.now() - 13 * 24 * 60 * 60 * 1000),
      metadata: {
        status: 'resolved',
      },
    },
    {
      id: 'mock-8',
      type: 'note',
      title: 'Internal note added',
      description: 'Customer has history of late payments. Recommend closer monitoring.',
      createdBy: 'Mike Chen',
      createdAt: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000),
    },
  ];
  return items.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
}

export function useTransactionRecentActivities(transactionId: number | undefined, transactionAmount: number = 0) {
  const { apiGet } = useApi();
  const mockActivities = buildMockActivities(transactionAmount);

  return useModeAwareQuery<TransactionActivity[], Error>(
    ['transaction-recent-activities', transactionId],
    async ({ signal }) => {
      if (!transactionId) return [];
      try {
        const response = await apiGet<GetRecentActivitiesApiResponse>(
          `/api/v1/recent-activities/transactions/${transactionId}`,
          { page: 1, pageSize: 50 },
          signal,
        );
        return response.data
          .map(mapApiToTransactionActivity)
          .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      } catch (error) {
        console.error('Error fetching transaction recent activities:', error);
        return [];
      }
    },
    mockActivities,
    {
      enabled: !!transactionId,
      staleTime: 0,
      refetchOnMount: 'always',
    },
  );
}
